import { pageTitle } from '@palaccount/config';
import type { PurchaseReturnFreeLineInput, PurchaseReturnReason } from '@palaccount/contracts';
import { Money } from '@palaccount/money';
import { Loader2, Plus, Send, Trash2 } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { Amount, Ltr } from '@/components/amount';
import { DeleteConfirmBox } from '@/components/confirm-box';
import { DocumentNumberChip } from '@/components/document-number-chip';
import { DocumentToolbar } from '@/components/document-toolbar';
import { useLineImport } from '@/features/documents/line-tools';
import { DocumentTotals, TwoWayPair } from '@/features/documents/document-totals';
import { DescriptionLangToggle, useDescriptionLanguage } from '@/features/documents/description-language';
import { Gated } from '@/components/permission-gate';
import { ReasonBox } from '@/components/reason-box';
import { PurchaseReturnStatusChip } from '@/components/status-chip';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Typeahead } from '@/components/ui/typeahead';
import { useCashAccountOptions } from '@/features/accounts/api';
import { useOrgSetting } from '@/features/admin/api';
import { FxPanel, useRateWarningGate } from '@/features/documents/fx-panel';
import { RateField, useRateInput } from '@/features/documents/rate-field';
import { DocCurrencyCell, useDocCurrency } from '@/features/documents/doc-currency';
import { ItemCell } from '@/features/inventory/item-cell';
import { useItemPicker } from '@/features/inventory/item-picker';
import { useWarehouses } from '@/features/inventory/stock-api';
import { SupplierBalanceChip } from '@/features/partners/supplier-balance-chip';
import { usePartnerPicker } from '@/features/partners/partner-picker';
import { PartnerTypeahead } from '@/features/partners/partner-typeahead';
import { computeTotalsMirror, type TwoWayState } from '@/features/sales/totals-mirror';
import {
  captureBlocks,
  textFromStored,
  trackingPayload,
  type CaptureText,
  type TrackedItem,
} from '@/features/sales/tracking-capture';
import { TrackingRows } from '@/features/sales/tracking-rows';
import { denyPermission, useCan } from '@/lib/permissions';
import { ApiError, localizeServerCause, localizeServerMessage } from '@/lib/server-messages';
import { useBaseCurrency, useDocumentDate } from '@/lib/session';
import { cn } from '@/lib/utils';

import { useBillPicker } from './api';
import { isPurchaseReturnEditable, trimQty } from './return-caps';
import { CellInput } from './return-editor-page';
import {
  EMPTY_RETURN_HEADER,
  ReturnHeaderFields,
  type ReturnHeaderValues,
} from './return-header-fields';
import {
  useCancelPurchaseReturn,
  useCreatePurchaseReturn,
  useDeletePurchaseReturn,
  usePostPurchaseReturn,
  usePurchaseReturn,
  useRefundPurchaseReturn,
  useSupplierItemHistory,
  useUpdatePurchaseReturn,
} from './returns-api';

const REASONS: PurchaseReturnReason[] = [
  'damaged_goods',
  'wrong_item_shipped',
  'customer_changed_mind',
  'overstock',
  'other',
];

/** One free line of a DIRECT return (wave 5D). */
interface FreeRow {
  key: number;
  itemId: string;
  warehouseId: string;
  description: string;
  qty: string;
  unitPrice: string;
  discountPct: string;
  /** BR-MD-03-17 on the way back out — the same sub-table the receipt used. */
  tracking: CaptureText;
  /**
   * Whether the clerk has TYPED the price. While false, the supplier's last
   * billed price for the item (`useSupplierItemHistory`) may replace the
   * catalogue cost once it arrives; a typed price, or a stored line's price,
   * is never overwritten by a lookup that lands later.
   */
  priceTouched: boolean;
}

let nextKey = 1;
const blankRow = (): FreeRow => ({
  key: nextKey++,
  itemId: '',
  warehouseId: '',
  description: '',
  qty: '',
  unitPrice: '',
  discountPct: '',
  tracking: {},
  priceTouched: false,
});

/** `{pct|amount}` for the wire from the two-way box, or nothing when empty. */
function twoWayBody(state: TwoWayState | undefined): { pct: string } | { amount: string } | undefined {
  const value = state?.value.trim() ?? '';
  if (!state || value === '') return undefined;
  return state.basis === 'pct' ? { pct: value } : { amount: value };
}

/**
 * The per-line history hint — its own component because it holds a query
 * per line. It asks what we BOUGHT from this supplier of this item and sent
 * back, hands the last billed price up to the row while the clerk has not
 * typed one, and warns — never blocks — when more goes back than was ever
 * bought. A direct return has no bill to cap against, so the figure is a
 * hint beside the box, not a refusal.
 */
function LineHistory({
  partnerId,
  row,
  currency,
  editable,
  onDefaultPrice,
}: {
  partnerId: string;
  row: FreeRow;
  currency: string;
  editable: boolean;
  onDefaultPrice: (key: number, price: string) => void;
}) {
  const { t } = useTranslation();
  const { data: history } = useSupplierItemHistory(partnerId, row.itemId);
  const lastPrice =
    history?.lastPrice !== null &&
    history?.lastPrice !== undefined &&
    history.lastPriceCurrency === currency
      ? history.lastPrice
      : null;

  useEffect(() => {
    // Settles after one pass: once the row holds the last price (or the clerk
    // has typed one) the guard is false and nothing is written again.
    if (editable && !row.priceTouched && lastPrice !== null && row.unitPrice !== lastPrice) {
      onDefaultPrice(row.key, lastPrice);
    }
  }, [lastPrice, row.key, row.unitPrice, row.priceTouched, editable, onDefaultPrice]);

  if (!history) return null;
  // Quantities, not money: Number() is the comparison DECISION.md §2.1 allows
  // for a hint, and the server re-reads the same figures on post.
  const qty = Number(row.qty);
  const available = Number(history.qtyBought) - Number(history.qtyReturned);
  const overBought = Number.isFinite(qty) && qty > 0 && qty > available;
  if (!overBought) return null;
  return (
    <p
      role="status"
      className="mt-1 rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200"
    >
      {t('preturn.overBoughtHint', {
        bought: trimQty(history.qtyBought),
        returned: trimQty(history.qtyReturned),
      })}
    </p>
  );
}

/**
 * Wave 5D — the DIRECT purchase return: a debit note with no bill behind it.
 * Goods are going back to a supplier and nobody can point at the bill they
 * came in on, so the return names the supplier itself and FREE lines: an
 * item (the picker), where it leaves from, a quantity, a unit price that
 * defaults to the supplier's last billed price (else the item's cost price)
 * and stays editable.
 *
 * Nothing here is capped — there is no billed quantity to cap against — and
 * nothing is copied from a bill, which is why this is its own component
 * beside `return-editor-page.tsx` rather than a mode inside it: that
 * editor's every column is a cap figure or a copied price, and the
 * bill-linked rules (BR-PUR-04-04/-06, §15's bill-total cap) stay untouched
 * over there. Posting (purchasing.approve) debits the payable, relieves
 * stock at the location's current cost (no inbound layer to reverse), and
 * taxes at the company default unless the VAT box is typed.
 *
 * The door BACK to the bill-linked shape is here too: "Return against a
 * bill" over the chosen supplier's POSTED bills (BR-PUR-04-03) navigates to
 * `?billId=`, and the route draws the other editor with the bill's lines
 * loaded and capped. Nothing typed here is kept: a return against a bill
 * copies the bill, it does not merge with a draft.
 */
export function DirectPurchaseReturnEditor({ returnId }: { returnId: string | undefined }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isNew = !returnId;

  const { data: record, isPending: loadingReturn } = usePurchaseReturn(returnId);
  const createReturn = useCreatePurchaseReturn();
  const updateReturn = useUpdatePurchaseReturn(returnId ?? '');
  const deleteReturn = useDeletePurchaseReturn(returnId ?? '');
  const postReturn = usePostPurchaseReturn(returnId ?? '');
  const cancelReturn = useCancelPurchaseReturn(returnId ?? '');
  const refundReturn = useRefundPurchaseReturn(returnId ?? '');
  const { options: cashOptions } = useCashAccountOptions();
  const [refundAccountId, setRefundAccountId] = useState('');

  const canEdit = useCan(isNew ? 'purchasing.create' : 'purchasing.edit');
  const canPost = useCan('purchasing.approve');
  const canEditNotes = useCan('purchasing.edit');

  const [returnDate, setReturnDate] = useDocumentDate();
  const [partnerId, setPartnerId] = useState('');
  const [reason, setReason] = useState<PurchaseReturnReason>('damaged_goods');
  const [reasonNote, setReasonNote] = useState('');
  const [restocked, setRestocked] = useState(true);
  const [valueOnly, setValueOnly] = useState(false);
  const [header, setHeader] = useState<ReturnHeaderValues>(EMPTY_RETURN_HEADER);
  const [rows, setRows] = useState<FreeRow[]>(() => [blankRow()]);
  // EC-19-73: the VAT box opens at the COMPANY default rate; typed, it is
  // sent as the document's own box (BR-SAL-00-14 — settings never reach back).
  const defaultTaxPct = useOrgSetting<string>('invoicing.default_vat_pct');
  const [taxPct, setTaxPct] = useState<string | null>(null);
  const [discount, setDiscount] = useState<TwoWayState | undefined>(undefined);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [cancelAsk, setCancelAsk] = useState(false);
  const { confirmRateWarning, rateWarningDialog } = useRateWarningGate();
  const loadedId = useRef<string | null>(null);

  // GEN-50 — the typeaheads: the supplier, and the items these rows name.
  const supplierPicker = usePartnerPicker(partnerId || null, { type: 'supplier' });
  const itemPicker = useItemPicker(
    useMemo(() => rows.map((row) => row.itemId), [rows]),
    { status: 'active' },
  );
  const { data: warehouses } = useWarehouses();
  const activeWarehouses = (warehouses ?? []).filter((warehouse) => warehouse.isActive);
  // The door back to the bill-linked shape: THAT supplier's POSTED bills only
  // (BR-PUR-04-03), searched on the server, off until a supplier is chosen.
  const billPicker = useBillPicker(
    null,
    partnerId ? { partnerId, status: 'posted' } : { status: 'posted' },
  );

  const status = record?.status ?? 'draft';
  const editable = isPurchaseReturnEditable(status) && canEdit;
  const partner = supplierPicker.byId.get(partnerId);
  // GEN-108's chain: the document's own code, else the supplier's, else base.
  const base = useBaseCurrency();
  // Wave 4D — the editable rate (features/documents/rate-field).
  const rate = useRateInput(record);
  // Per-document currency (2026-10-30): the header's dropdown in front of the chain.
  const docCurrency = useDocCurrency({
    partnerId,
    savedCurrency: record?.currency,
    partnerCurrency: partner?.currency ?? base,
    onPick: () => rate.setText(''),
  });
  const currency = docCurrency.currency;

  useEffect(() => {
    document.title = pageTitle(t('preturn.editorTitle'));
  }, [t]);

  // An existing note: adopt the stored document.
  useEffect(() => {
    if (record && loadedId.current !== record.id) {
      loadedId.current = record.id;
      setReturnDate(record.returnDate);
      setPartnerId(record.partnerId);
      setReason(record.reason);
      setReasonNote(record.reasonNote ?? '');
      setRestocked(record.restocked);
      setValueOnly(record.valueOnly);
      setNotes(record.notes ?? '');
      setHeader({
        manualRef: record.manualRef ?? '',
        bookNo: record.bookNo ?? '',
        buyerUserId: record.buyerUserId ?? '',
        returnsAccountId: record.returnsAccountId ?? '',
      });
      setTaxPct(record.taxPct.replace(/\.00$/, ''));
      setDiscount(
        record.discountPct !== '0.00'
          ? { basis: 'pct', value: record.discountPct.replace(/\.00$/, '') }
          : record.discountAmount !== '0.00'
            ? { basis: 'amount', value: record.discountAmount }
            : undefined,
      );
      setRows(
        record.lines.map((line) => ({
          key: nextKey++,
          itemId: line.itemId ?? '',
          warehouseId: line.warehouseId ?? '',
          description: line.description,
          qty: trimQty(line.qty),
          unitPrice: line.unitPrice,
          discountPct: line.discountPct.replace(/\.00$/, ''),
          tracking: textFromStored(line),
          // A stored price is the document's own — no lookup overwrites it.
          priceTouched: true,
        })),
      );
    }
  }, [record]);

  const effectiveTaxPct = taxPct ?? defaultTaxPct;
  const totals = useMemo(
    () =>
      computeTotalsMirror(
        rows.map((row) => ({ qty: row.qty, unitPrice: row.unitPrice, discountPct: row.discountPct })),
        discount,
        { basis: 'pct', value: effectiveTaxPct },
        currency,
        defaultTaxPct,
      ),
    [rows, discount, effectiveTaxPct, currency, defaultTaxPct],
  );

  const setRow = (key: number, patch: Partial<FreeRow>) =>
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...patch } : row)));

  /**
   * The picker's item fills what the clerk has not typed: its name, its cost
   * price. The supplier's LAST billed price for it, when there is one,
   * replaces the catalogue figure a moment later (`LineHistory`) — unless
   * typed first.
   */
  const pickItem = (key: number, itemId: string) => {
    if (!itemId) {
      setRow(key, { itemId: '', warehouseId: '', unitPrice: '', tracking: {}, priceTouched: false });
      return;
    }
    const item = itemPicker.byId.get(itemId);
    setRow(key, {
      itemId,
      description: item ? descLang.describe(item) : '',
      // `costPrice` is withheld without `inventory.see_item_cost`; the box
      // then waits for the supplier's last price, or the clerk's own figure.
      unitPrice:
        item?.costPrice !== undefined
          ? Money.of(item.costPrice, currency).rounded().toFixed2()
          : '',
      tracking: {},
      priceTouched: false,
    });
  };

  const defaultPrice = (key: number, price: string) =>
    setRows((current) =>
      current.map((row) =>
        row.key === key && !row.priceTouched ? { ...row, unitPrice: price } : row,
      ),
    );

  // BR-SAL-00-20: removing the last line leaves one blank line.
  const removeRow = (key: number) =>
    setRows((current) => {
      const left = current.filter((row) => row.key !== key);
      return left.length === 0 ? [blankRow()] : left;
    });

  /**
   * `expandKits: false` on purpose, as the bill-linked editor does: a goods
   * receipt takes a bundle in as ITSELF (PUR-02 never resolves a kit), so the
   * return sends back the same thing that arrived.
   */
  const blocksFor = (row: FreeRow) =>
    captureBlocks(itemPicker.byId.get(row.itemId) as TrackedItem | undefined, row.qty, {
      expandKits: false,
    });

  const requestLines: PurchaseReturnFreeLineInput[] = useMemo(
    () =>
      rows
        .filter((row) => row.itemId && /^\d+(\.\d+)?$/.test(row.qty.trim()) && Number(row.qty) > 0)
        .map((row) => ({
          itemId: row.itemId,
          ...(row.warehouseId ? { warehouseId: row.warehouseId } : {}),
          ...(row.description.trim() ? { description: row.description.trim() } : {}),
          qty: row.qty.trim(),
          ...(row.unitPrice.trim() ? { unitPrice: row.unitPrice.trim() } : {}),
          ...(row.discountPct.trim() ? { discountPct: row.discountPct.trim() } : {}),
          // §15: a value-only return moves no stock, so nothing is named.
          ...(valueOnly ? {} : trackingPayload(blocksFor(row), row.tracking)),
        })),
    // `blocksFor` closes over the picker's items, which is why they are listed.
    [rows, valueOnly, itemPicker.byId],
  );

  const validate = (): string | null => {
    if (!partnerId) return t('preturn.errPickSupplier');
    if (requestLines.length === 0) return t('preturn.errNoLines');
    return null;
  };

  const handleFailure = (
    thrown: unknown,
    permission: 'purchasing.create' | 'purchasing.edit' | 'purchasing.delete' | 'purchasing.approve',
  ) => {
    if (thrown instanceof ApiError && thrown.status === 403) denyPermission(permission);
    setError(localizeServerCause(t, thrown, 'preturn.errSaveFailed'));
  };

  /** Wave 4C — lines from a small .xlsx/.csv; a direct return sends goods back, so every row needs a known item code. */
  /** Wave 4C — which name a picked/imported item writes into a NEW line description. */
  const descLang = useDescriptionLanguage();
  const lineImport = useLineImport({
    id: 'purchase-return',
    side: 'purchase',
    describe: descLang.describe,
    copyLines: () =>
      rows
        .filter((line) => line.itemId !== '' || line.description.trim() !== '')
        .map((line) => ({
          code: itemPicker.byId.get(line.itemId)?.code ?? '',
          description: line.description,
          qty: line.qty,
          unitPrice: line.unitPrice,
          discountPct: line.discountPct,
        })),
    requireItem: true,
    visible: isPurchaseReturnEditable(status),
    gate: {
      permission: isNew ? 'purchasing.create' : 'purchasing.edit',
      reasonKey: isNew ? 'perm.cannot.createPurchaseReturns' : 'perm.cannot.editPurchaseReturns',
    },
    disabled: busy || !editable,
    onLines: (imported) =>
      setRows((current) => [
        ...current.filter((row) => row.itemId !== '' || row.qty.trim() !== ''),
        ...imported.map((line) => ({
          ...blankRow(),
          itemId: line.itemId,
          description: line.description,
          qty: line.qty,
          unitPrice: line.unitPrice,
          discountPct: line.discountPct,
          priceTouched: true,
        })),
      ]),
  });

  const save = async (navigateAfter = true): Promise<string | null> => {
    setError(null);
    const problem = validate();
    if (problem) {
      setError(problem);
      return null;
    }
    const tax = effectiveTaxPct.trim() === '' ? undefined : { pct: effectiveTaxPct.trim() };
    // F-2: the typed box rides as typed; a cleared box on a saved draft sends
    // an explicit zero, because the server keeps the stored discount unless
    // re-typed (BR-SAL-00-14) and "cleared" IS a re-type.
    const discountBody = twoWayBody(discount) ?? (isNew ? undefined : { pct: '0' });
    try {
      if (isNew) {
        const created = await createReturn.mutateAsync({
          ...rate.createBody(),
          ...docCurrency.body(),
          partnerId,
          returnDate,
          reason,
          ...(reasonNote.trim() ? { reasonNote: reasonNote.trim() } : {}),
          restocked,
          valueOnly,
          lines: requestLines,
          ...(discountBody ? { discount: discountBody } : {}),
          ...(tax ? { tax } : {}),
          ...(header.manualRef.trim() ? { manualRef: header.manualRef.trim() } : {}),
          ...(header.bookNo.trim() ? { bookNo: header.bookNo.trim() } : {}),
          ...(header.buyerUserId ? { buyerUserId: header.buyerUserId } : {}),
          ...(header.returnsAccountId ? { returnsAccountId: header.returnsAccountId } : {}),
          ...(notes.trim() ? { notes } : {}),
        });
        if (navigateAfter) void navigate(`/purchases/returns/${created.id}`, { replace: true });
        return created.id;
      }
      await updateReturn.mutateAsync({
        ...rate.patchBody(),
        ...docCurrency.body(),
        returnDate,
        reason,
        reasonNote: reasonNote.trim() === '' ? null : reasonNote.trim(),
        restocked,
        valueOnly,
        lines: requestLines,
        ...(discountBody ? { discount: discountBody } : {}),
        ...(tax ? { tax } : {}),
        manualRef: header.manualRef.trim() === '' ? null : header.manualRef.trim(),
        bookNo: header.bookNo.trim() === '' ? null : header.bookNo.trim(),
        buyerUserId: header.buyerUserId === '' ? null : header.buyerUserId,
        returnsAccountId: header.returnsAccountId === '' ? null : header.returnsAccountId,
        notes: notes.trim() === '' ? null : notes,
      });
      loadedId.current = null;
      return returnId!;
    } catch (thrown) {
      handleFailure(thrown, isNew ? 'purchasing.create' : 'purchasing.edit');
      return null;
    }
  };

  const onSave = async () => {
    setBusy(true);
    await save();
    setBusy(false);
  };

  /** Post return — saves first, then posts (the bill-linked editor's shape). */
  const onPost = async () => {
    if (!(await confirmRateWarning(record?.rateWarning))) return;
    setBusy(true);
    setError(null);
    try {
      const id = await save(false);
      if (!id) return;
      if (isNew) {
        const { api } = await import('@/api');
        const response = await api.purchaseReturns.post({ params: { returnId: id }, body: {} });
        if (response.status !== 200) {
          if (response.status === 403) denyPermission('purchasing.approve');
          const body = response.body;
          const sentence =
            typeof body === 'object' && body !== null && 'message' in body
              ? String((body as { message: unknown }).message)
              : t('common.loadFailed');
          setError(localizeServerMessage(t, sentence, new ApiError(response.status, sentence, body)));
          return;
        }
        void navigate(`/purchases/returns/${id}`, { replace: true });
        return;
      }
      await postReturn.mutateAsync();
      loadedId.current = null;
    } catch (thrown) {
      handleFailure(thrown, 'purchasing.approve');
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    setDeleting(false);
    if (!returnId) return;
    setBusy(true);
    setError(null);
    try {
      await deleteReturn.mutateAsync();
      void navigate('/purchases/returns');
    } catch (thrown) {
      handleFailure(thrown, 'purchasing.delete');
    } finally {
      setBusy(false);
    }
  };

  const onSaveNotes = async () => {
    setBusy(true);
    setError(null);
    try {
      await updateReturn.mutateAsync({ notes: notes.trim() === '' ? null : notes });
      loadedId.current = null;
    } catch (thrown) {
      handleFailure(thrown, 'purchasing.edit');
    } finally {
      setBusy(false);
    }
  };

  /** BR-PUR-04-01 / GEN-72: cancelling a posted debit note asks WHY. */
  const onCancelPosted = async (why: string) => {
    if (!returnId) return;
    setCancelAsk(false);
    setBusy(true);
    setError(null);
    try {
      await cancelReturn.mutateAsync(why);
      loadedId.current = null;
    } catch (thrown) {
      handleFailure(thrown, 'purchasing.approve');
    } finally {
      setBusy(false);
    }
  };

  /** BR-PUR-04-02 / PUR-04 §12 row 4: the supplier sent money back. */
  const onRefund = async () => {
    if (!returnId || !refundAccountId) return;
    setBusy(true);
    setError(null);
    try {
      await refundReturn.mutateAsync(refundAccountId);
      loadedId.current = null;
    } catch (thrown) {
      if (thrown instanceof ApiError && thrown.status === 403)
        denyPermission('accounting.approve');
      setError(localizeServerCause(t, thrown, 'preturn.errSaveFailed'));
    } finally {
      setBusy(false);
    }
  };

  if (!isNew && loadingReturn) {
    return <div className="h-64 animate-pulse rounded-xl bg-muted" aria-busy />;
  }

  // A saved note's stored block is authoritative while it is locked; a draft
  // shows the live mirror. F-3's taxable base is the subtraction, drawn once.
  const shown =
    record && !editable
      ? {
          subtotal: record.subtotal,
          discountAmount: record.discountAmount,
          taxAmount: record.taxAmount,
          grandTotal: record.grandTotal,
        }
      : {
          subtotal: totals.subtotal,
          discountAmount: totals.discountAmount ?? '0.00',
          taxAmount: totals.taxAmount ?? '0.00',
          grandTotal: totals.grandTotal ?? totals.subtotal,
        };

  return (
    <div className="grid gap-4">
      {rateWarningDialog}
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-3 bg-background">
        <h2 className="text-lg font-bold">{t('preturn.editorTitle')}</h2>
        <DocumentNumberChip
          label={t('preturn.colNo')}
          code={record?.code ?? null}
          fallback={t('preturn.codeNew')}
        />
        <PurchaseReturnStatusChip status={status} />
        {/* The strip's source pill says so: there is no bill behind this one. */}
        <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
          {t('preturn.noBill')}
        </span>
        <DocumentToolbar
          actions={[
            {
              id: 'back',
              row: null,
              label: t('common.close'),
              variant: 'outline',
              to: '/purchases/returns',
            },
            // Wave 4C — Import lines / Download template (shared line tools).
            ...lineImport.acts,
            {
              id: 'delete',
              row: 'delete',
              label: t('common.delete'),
              icon: <Trash2 aria-hidden />,
              variant: 'destructive',
              visible: !isNew && status === 'draft',
              gate: {
                permission: 'purchasing.delete',
                reasonKey: 'perm.cannot.deletePurchaseReturns',
              },
              disabled: busy,
              onAct: () => setDeleting(true),
            },
            {
              id: 'applyCredit',
              row: null,
              label: t('preturn.applyCredit'),
              visible: status !== 'draft',
              to: `/accounting/payments/new?partnerId=${record?.partnerId ?? ''}&creditNoteId=${returnId ?? ''}`,
            },
            {
              id: 'save',
              row: 'save',
              label: t('common.save'),
              visible: isPurchaseReturnEditable(status),
              gate: {
                permission: isNew ? 'purchasing.create' : 'purchasing.edit',
                reasonKey: isNew
                  ? 'perm.cannot.createPurchaseReturns'
                  : 'perm.cannot.editPurchaseReturns',
              },
              busy,
              onAct: () => void onSave(),
            },
            {
              id: 'cancelPosted',
              row: 'cancel',
              label: t('preturn.cancelPosted'),
              icon: <Trash2 aria-hidden />,
              variant: 'destructive',
              visible: status === 'posted',
              gate: {
                permission: 'purchasing.approve',
                reasonKey: 'perm.cannot.cancelPurchaseReturns',
              },
              disabled: busy,
              onAct: () => setCancelAsk(true),
            },
          ]}
        >
          {!isNew && status === 'draft' ? (
            <DeleteConfirmBox
              open={deleting}
              detail={t('preturn.confirmDelete')}
              onCancel={() => setDeleting(false)}
              onConfirm={() => void onDelete()}
              busy={busy}
            />
          ) : null}
          <ReasonBox
            open={cancelAsk}
            title={t('preturn.cancelPosted')}
            prompt={t('preturn.cancelPrompt')}
            reasonLabel={t('gen72.cancelReasonLabel')}
            confirmLabel={t('gen72.confirmCancel')}
            busy={busy}
            onConfirm={(why) => void onCancelPosted(why)}
            onCancel={() => setCancelAsk(false)}
          />
        </DocumentToolbar>
      </div>

      {status === 'posted' ? (
        <p className="rounded-md border border-blue-300 bg-blue-50 px-4 py-3 text-sm text-blue-900 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-200">
          {t('preturn.postedNotice')}
        </p>
      ) : null}
      {status === 'posted' ? (
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">
          <span className="me-auto">{t('preturn.refundHint')}</span>
          <NativeSelect
            aria-label={t('preturn.refundAccount')}
            value={refundAccountId}
            onChange={(event) => setRefundAccountId(event.target.value)}
            className="w-auto"
          >
            <option value="">{t('receipts.pickAccount')}</option>
            {cashOptions.map((option) => (
              <option key={option.id} value={option.id}>
                {option.code} — {option.name}
              </option>
            ))}
          </NativeSelect>
          <Gated permission="accounting.approve" reasonKey="perm.cannot.refundPurchaseReturns">
            <Button size="sm" variant="outline" onClick={() => void onRefund()} disabled={busy || !refundAccountId}>
              {t('preturn.refund')}
            </Button>
          </Gated>
        </div>
      ) : null}
      {status === 'refunded' ? (
        <p className="rounded-md border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">
          {t('preturn.refundedNotice')}
        </p>
      ) : null}
      {status === 'cancelled' ? (
        <p className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
          {t('preturn.cancelledNotice', { reason: record?.cancelReason ?? '' })}
        </p>
      ) : null}
      {/* Wave 4C — the line import's chooser, summary and per-row problems. */}
      {lineImport.panel}
      {error ? (
        <p
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          {error}
        </p>
      ) : null}

      {/* ── The details line: date, supplier, reason, the flags, the header ── */}
      <Card>
        <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="grid gap-1.5">
            <Label htmlFor="dbn-date">{t('common.date')}</Label>
            <Input
              id="dbn-date"
              type="date"
              dir="ltr"
              value={returnDate}
              onChange={(event) => setReturnDate(event.target.value)}
              disabled={!editable}
            />
          </div>
          {/* Per-document currency (2026-10-30): a draft return may pick its own. */}
          <DocCurrencyCell
            idPrefix="dbn"
            value={currency}
            onChange={docCurrency.pick}
            disabled={!editable || status !== 'draft'}
          />
          {/* Wave 4D — the editable rate (features/documents/rate-field). */}
          <RateField idPrefix="dbn" currency={currency} doc={record} input={rate} disabled={!editable} />
          <div className="grid gap-1.5">
            <Label htmlFor="dbn-supplier">{t('preturn.fSupplier')}</Label>
            {isNew ? (
              <PartnerTypeahead
                entity="supplier"
                id="dbn-supplier"
                label={t('preturn.fSupplier')}
                value={partnerId || null}
                onChange={(id) => setPartnerId(id ?? '')}
                options={supplierPicker.options}
                selected={supplierPicker.optionFor(partnerId)}
                query={supplierPicker.query}
                onQueryChange={supplierPicker.setQuery}
                placeholder={t('preturn.pickSupplierPh')}
                emptyLabel={t('picker.noMatchingSupplier')}
                loading={supplierPicker.loading}
              />
            ) : (
              <div
                id="dbn-supplier"
                className="flex h-12 items-center gap-3 rounded-[11px] border border-input bg-muted/40 px-3 text-sm"
              >
                <span className="truncate font-medium">{record?.partnerName ?? ''}</span>
                <Ltr value={currency} className="ms-auto font-mono text-muted-foreground" />
              </div>
            )}
            {/* Wave 5D: what we owe them, beside the note that lowers it. */}
            <SupplierBalanceChip partnerId={partnerId || null} side="supplier" />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="dbn-reason">{t('preturn.fReason')}</Label>
            <NativeSelect
              id="dbn-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value as PurchaseReturnReason)}
              disabled={!editable}
            >
              {REASONS.map((value) => (
                <option key={value} value={value}>
                  {t(`returns.reason.${value}`)}
                </option>
              ))}
            </NativeSelect>
          </div>
          <div className="grid gap-1.5">
            {/* BR-PUR-04-08 — the flag that decides the §12 goods leg. */}
            <Label htmlFor="dbn-restocked">{t('preturn.fRestocked')}</Label>
            <label className="flex h-12 items-center gap-3 rounded-[11px] border border-input px-3 text-sm">
              <Checkbox
                id="dbn-restocked"
                checked={restocked}
                onCheckedChange={(checked) => setRestocked(checked === true)}
                disabled={!editable}
              />
              <span className="text-muted-foreground">
                {restocked ? t('preturn.restockedOn') : t('preturn.restockedOff')}
              </span>
            </label>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="dbn-value-only">{t('preturn.fValueOnly')}</Label>
            <label className="flex h-12 items-center gap-3 rounded-[11px] border border-input px-3 text-sm">
              <Checkbox
                id="dbn-value-only"
                checked={valueOnly}
                onCheckedChange={(checked) => setValueOnly(checked === true)}
                disabled={!editable}
              />
              <span className="text-muted-foreground">{t('preturn.valueOnlyHint')}</span>
            </label>
          </div>
          {reason === 'other' ? (
            <div className="grid gap-1.5 sm:col-span-2 lg:col-span-3">
              <Label htmlFor="dbn-reason-note">{t('preturn.fReasonNote')}</Label>
              <Input
                id="dbn-reason-note"
                value={reasonNote}
                maxLength={500}
                placeholder={t('preturn.reasonNotePh')}
                onChange={(event) => setReasonNote(event.target.value)}
                disabled={!editable}
              />
            </div>
          ) : null}
          <ReturnHeaderFields
            values={header}
            onChange={(patch) => setHeader((current) => ({ ...current, ...patch }))}
            editable={editable}
            idPrefix="dbn"
          />
          {isNew ? (
            <div className="grid gap-1.5 sm:col-span-2">
              {/* The door to the bill-linked shape: pick one of THIS supplier's
                  posted bills and the route redraws the other editor with its
                  lines loaded and capped. Nothing typed here is kept. */}
              <Label htmlFor="dbn-source-bill">{t('preturn.fSourceBill')}</Label>
              <Typeahead
                id="dbn-source-bill"
                label={t('preturn.fSourceBill')}
                value={null}
                onChange={(id) => {
                  if (id) void navigate(`/purchases/returns/new?billId=${id}`);
                }}
                options={billPicker.options}
                selected={null}
                query={billPicker.query}
                onQueryChange={billPicker.setQuery}
                placeholder={t('preturn.pickBillPh')}
                emptyLabel={t('picker.noMatchingBill')}
                loading={billPicker.loading}
                disabled={partnerId === ''}
              />
              <p className="text-xs text-muted-foreground">{t('preturn.pickBillDoorHint')}</p>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* ── The free-line grid ───────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle>{t('preturn.linesTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-11 text-center">#</TableHead>
                <TableHead className="min-w-[150px]">{t('quotes.colItem')}</TableHead>
                <TableHead className="min-w-[130px]">{t('orders.colWarehouse')}</TableHead>
                <TableHead className="min-w-[200px]">{t('sales.colDescription')}
                  {editable ? <DescriptionLangToggle language={descLang} /> : null}
                </TableHead>
                <TableHead className="w-24 text-end">{t('sales.colQty')}</TableHead>
                <TableHead className="w-32 text-end">{t('sales.colUnitPrice')}</TableHead>
                <TableHead className="w-24 text-end">{t('sales.colDisc')}</TableHead>
                <TableHead className="w-32 text-end">{t('sales.colTotal')}</TableHead>
                {editable ? <TableHead className="w-11" /> : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row, index) => {
                const view = totals.lines[index];
                return (
                  <React.Fragment key={row.key}>
                    <TableRow className="odd:bg-muted/30">
                      <TableCell className="text-center font-mono text-muted-foreground">
                        {index + 1}
                      </TableCell>
                      <TableCell>
                        {/* A direct return sends back GOODS, so every line names an item. */}
                        <ItemCell
                          label={t('quotes.cellItem', { line: index + 1 })}
                          picker={itemPicker}
                          value={row.itemId}
                          onPick={(itemId) => pickItem(row.key, itemId)}
                          disabled={!editable}
                        />
                        {partnerId && row.itemId ? (
                          <LineHistory
                            partnerId={partnerId}
                            row={row}
                            currency={currency}
                            editable={editable}
                            onDefaultPrice={defaultPrice}
                          />
                        ) : null}
                      </TableCell>
                      <TableCell>
                        {row.itemId ? (
                          <NativeSelect
                            aria-label={t('orders.cellWarehouse', { line: index + 1 })}
                            className="h-9 text-sm"
                            value={row.warehouseId}
                            onChange={(event) => setRow(row.key, { warehouseId: event.target.value })}
                            disabled={!editable}
                          >
                            <option value="">{t('sales.pickWarehouse')}</option>
                            {activeWarehouses.map((warehouse) => (
                              <option key={warehouse.id} value={warehouse.id}>
                                {warehouse.name}
                              </option>
                            ))}
                          </NativeSelect>
                        ) : (
                          <span className="px-2 text-sm text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <CellInput
                          aria-label={t('sales.cellDescription', { line: index + 1 })}
                          value={row.description}
                          maxLength={200}
                          onChange={(event) => setRow(row.key, { description: event.target.value })}
                          disabled={!editable}
                        />
                      </TableCell>
                      <TableCell>
                        <CellInput
                          mono
                          dir="ltr"
                          inputMode="decimal"
                          aria-label={t('sales.cellQty', { line: index + 1 })}
                          value={row.qty}
                          placeholder="0"
                          onChange={(event) => setRow(row.key, { qty: event.target.value })}
                          disabled={!editable}
                        />
                      </TableCell>
                      <TableCell>
                        {/* Defaults to the supplier's last billed price for
                            the item, else the item's cost price — and editable. */}
                        <CellInput
                          mono
                          dir="ltr"
                          inputMode="decimal"
                          aria-label={t('sales.cellUnitPrice', { line: index + 1 })}
                          value={row.unitPrice}
                          placeholder="0.00"
                          onChange={(event) =>
                            setRow(row.key, { unitPrice: event.target.value, priceTouched: true })
                          }
                          disabled={!editable}
                        />
                      </TableCell>
                      <TableCell>
                        <CellInput
                          mono
                          dir="ltr"
                          inputMode="decimal"
                          aria-label={t('sales.cellDisc', { line: index + 1 })}
                          value={row.discountPct}
                          placeholder="0"
                          onChange={(event) => setRow(row.key, { discountPct: event.target.value })}
                          disabled={!editable}
                        />
                      </TableCell>
                      <TableCell className="text-end">
                        {view?.lineTotal ? (
                          <Amount value={view.lineTotal} currency={currency} />
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      {editable ? (
                        <TableCell>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label={t('sales.removeLine', { line: index + 1 })}
                            title={t('sales.removeLine', { line: index + 1 })}
                            onClick={() => removeRow(row.key)}
                          >
                            <Trash2 aria-hidden />
                          </Button>
                        </TableCell>
                      ) : null}
                    </TableRow>
                    {!valueOnly && row.itemId ? (
                      <TrackingRows
                        blocks={blocksFor(row)}
                        text={row.tracking}
                        onChange={(key, value) =>
                          setRow(row.key, { tracking: { ...row.tracking, [key]: value } })
                        }
                        disabled={!editable}
                        columns={editable ? 9 : 8}
                        line={index + 1}
                      />
                    ) : null}
                  </React.Fragment>
                );
              })}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell colSpan={editable ? 9 : 8} className="text-end text-sm">
                  <span className="text-muted-foreground">{t('orders.netTaxTotal')}</span>{' '}
                  <Amount value={shown.subtotal} currency={currency} /> {' / '}
                  <Amount value={shown.taxAmount} currency={currency} /> {' / '}
                  <Amount value={shown.grandTotal} currency={currency} className="font-bold" />
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
          {editable ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-3"
              onClick={() => setRows((current) => [...current, blankRow()])}
            >
              <Plus aria-hidden /> {t('sales.addLine')}
            </Button>
          ) : null}
          <p className="mt-2 text-xs text-muted-foreground">{t('preturn.directHint')}</p>
        </CardContent>
      </Card>

      {/* ── Notes + the posting rail ─────────────────────────────────────── */}
      <div className="grid gap-4 lg:grid-cols-[1fr_380px]">
        <Card>
          <CardHeader>
            <CardTitle>{t('sales.notes')}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3">
            <Textarea
              aria-label={t('sales.notes')}
              maxLength={2000}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              disabled={!editable && !(status !== 'draft' && canEditNotes)}
            />
            {status !== 'draft' ? (
              <Gated
                permission="purchasing.edit"
                reasonKey="perm.cannot.editNotes"
                className="justify-self-start"
              >
                <Button variant="outline" onClick={() => void onSaveNotes()} disabled={busy}>
                  {t('sales.saveNotes')}
                </Button>
              </Gated>
            ) : null}
          </CardContent>
        </Card>

        <Card className="self-start">
          <CardContent className="grid gap-3">
            {/* Wave 4C — the shared totals rows; the VAT stays this screen's single % box. */}
            <DocumentTotals
              currency={currency}
              subtotal={shown.subtotal}
              discountAmount={shown.discountAmount}
              discountControl={
                <TwoWayPair
                  kind="discount"
                  state={discount}
                  onChange={setDiscount}
                  pct={totals.discountPct ?? '0.00'}
                  amount={shown.discountAmount ?? ''}
                  disabled={!editable}
                />
              }
              taxAmount={shown.taxAmount}
              vatControl={
                <span className="flex items-center gap-2">
                  <Input
                    aria-label={t('sales.vat')}
                    dir="ltr"
                    inputMode="decimal"
                    className={cn('h-8 w-20 text-end font-mono', !editable && 'opacity-70')}
                    value={effectiveTaxPct}
                    onChange={(event) => setTaxPct(event.target.value)}
                    disabled={!editable}
                  />
                  <span className="text-muted-foreground">%</span>
                  <Amount value={shown.taxAmount} currency={currency} />
                </span>
              }
              grandTotal={shown.grandTotal}
              totalLabel={t('preturn.debitTotal')}
            />

            <FxPanel doc={record} />

            {status === 'draft' ? (
              <Gated
                permission="purchasing.approve"
                reasonKey="perm.cannot.postPurchaseReturns"
                className="w-full"
              >
                <Button
                  className="h-[46px] w-full bg-emerald-700 text-white hover:bg-emerald-800"
                  onClick={() => void onPost()}
                  disabled={busy || requestLines.length === 0}
                >
                  {busy ? <Loader2 className="animate-spin" aria-hidden /> : <Send aria-hidden />}
                  {t('preturn.postReturn')}
                </Button>
              </Gated>
            ) : null}
            {status === 'draft' && !canPost ? (
              <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
                {t('preturn.approvalBanner')}
              </p>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
